/* Feature flag provider + hook.
 *
 * Reads `/api/me/features` on mount (and whenever ``projectId`` changes via
 * the optional setter exposed by ``useFeatureFlags()``). Exposes a flat map
 * for fast dotted-key lookup like:
 *
 *   const ff = useFeatureFlags();
 *   if (!ff.isOn("platform.brownfield_retrofit")) return null;
 *
 * The provider sits inside <AuthProvider> so it can read the session
 * cookie. If the user is not logged in the resolved flags are {}.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import api from "./api";

const FFCtx = createContext({
  ready: false,
  country: "IN",
  flagsFlat: {},
  feature_flags: {},
  domains: {},
  isOn: () => true,        // default-ON to avoid empty UI before fetch
  isOff: () => false,
  setProjectId: () => {},
  refresh: () => {},
});

export function FeatureFlagsProvider({ children }) {
  const [projectId, setProjectId] = useState(null);
  const [state, setState] = useState({
    ready: false,
    country: "IN",
    flagsFlat: {},
    feature_flags: {},
    domains: {},
  });

  const refresh = useCallback(async (pid) => {
    try {
      const target = pid !== undefined ? pid : projectId;
      const url = target
        ? `/me/features?project_id=${encodeURIComponent(target)}`
        : "/me/features";
      const { data } = await api.get(url);
      setState({
        ready: true,
        country: data?.context?.country || "IN",
        flagsFlat: data?.flags_flat || {},
        feature_flags: data?.feature_flags || {},
        domains: data?.domains_enabled || {},
      });
    } catch {
      // Unauthenticated or error — leave defaults; ready=true so UI renders.
      setState((s) => ({ ...s, ready: true }));
    }
  }, [projectId]);

  useEffect(() => { refresh(); }, [refresh]);

  const value = useMemo(() => ({
    ...state,
    isOn: (path, fallback = true) => {
      if (!state.ready) return fallback;
      const v = state.flagsFlat[path];
      return v === undefined ? fallback : Boolean(v);
    },
    isOff: (path, fallback = false) => {
      if (!state.ready) return fallback;
      const v = state.flagsFlat[path];
      return v === undefined ? fallback : !v;
    },
    setProjectId,
    refresh,
  }), [state, refresh]);

  return <FFCtx.Provider value={value}>{children}</FFCtx.Provider>;
}

export function useFeatureFlags() {
  return useContext(FFCtx);
}

/**
 * Renders children only when ``flag`` resolves truthy (or the inverse of
 * ``inverse``). Defaults to render if flags haven't loaded yet to avoid
 * flicker.
 *
 * <FeatureFlag flag="platform.brownfield_retrofit">
 *   <BrownfieldTab />
 * </FeatureFlag>
 *
 * <FeatureFlag flag="real_estate.deliverables.green_mark_minimum_certified">
 *   <GreenMarkScorecard />
 * </FeatureFlag>
 */
export function FeatureFlag({ flag, inverse = false, fallback = null, children }) {
  const ff = useFeatureFlags();
  const on = ff.isOn(flag, true);
  const show = inverse ? !on : on;
  return show ? children : fallback;
}
